import React, { useCallback } from 'react';
import { useForm } from '~/hooks';
import { useValidation } from '~/hooks/useValidation';
import { BsFillExclamationCircleFill } from 'react-icons/bs';
import { useSectionLoader } from '../../hooks';
import sectionTypes from '../sectionTypes';

/**
 * @typedef RecordedPlatReviewValues
 * @property {number}   sectionId
 * @property {string}   titleOpinionId * 
 * @typedef FormState
 * @property {RecordedPlatReviewValues} values The current form values
 */

/** @type {RecordedPlatReviewValues} */

const defaultState = {
    sectionId: 0
};

const validationSchema = {
    accessToPublicRightOfWay: {},
    sectionDetails: {
        validate: (field, data) => {
            if (typeof data.recordedPlat === 'string' && data.recordedPlat !== 'Yes') return true;
            return !!field?.[0]?._data?.book;
        }
    },
    accessType: {
        validate: (field, data) => {
            if (typeof data.accessToPublicRightOfWay === 'string' && data.accessToPublicRightOfWay !== 'Yes') return true;
            return field?.length > 0;
        }
    }
};

const RecordedPlatReview = ({ titleOpinionId, validate, validationResult }) => {
    const { onLoad: loadSection } = useSectionLoader(sectionTypes.RECORDED_PLAT, titleOpinionId);

    const onLoad = useCallback((values) => {
        return loadSection(values);
    }, [titleOpinionId]);

    const {
        /** @type {FormState} */
        state,
        //fieldActions
    } = useForm({
        /** @type {ReviewValues} */
        initialState: { ...defaultState },
        onLoad,
        debug: true
    });

    const bookDetails = state.values && state.values.sectionDetails ? state.values.sectionDetails.map((detail) => {
        const bookDetail = detail._data.book + ' ' + detail._data.pageNo;
        return bookDetail;
    }) : [];

    let errorSection = false;
    if (validate) {
        if (!state.values.sectionId) {
            errorSection = true;
        } else {
            if (state.values.sectionId !== 0 && state.values.recordedPlat === 'Yes') {
                for (const [key, value] of Object.entries(state.values)) {
                    if ((key !== 'additionalSection' && key !== 'orderNumber' && key !== 'errorMessage'
                        && !key.includes('feet')
                        && !key.includes('easement')
                        && !key.includes('documents')
                        && key != sectionTypes.RECORDED_PLAT
                        && (value === null || value === '') || (key === 'sectionDetails' && value.length === 0))
                    ) {
                        errorSection = true;
                        break;
                    }
                }

            }
            if (Object.prototype.hasOwnProperty.call(state.values, 'accessToPublicRightOfWay')) {
                if (state.values.accessToPublicRightOfWay.toLowerCase().trim() == "yes" && !Object.prototype.hasOwnProperty.call(state.values, 'accessType')) {
                    errorSection = true;
                } else {
                    errorSection = false;
                }
            }

            if (state.values.accessToPublicRightOfWay?.length > 0) {
                errorSection = false;
            } else {
                errorSection = true;
            }
        }

        validationResult(errorSection);
    }

    const [, invalidFields] = useValidation(state.values, validationSchema);

    return (
        <div className="ml-2 mr-5">
            {state.values.sectionId === 0 ?
                <div />
                :
                <>
                    <div className="col-span-12 border-t-2 border-blueGray-30 mt-4 mb-4" />
                    <div className="text-1xl text-blue-60 font-bold mb-3">
                        <div className="display:flex; flex-direction:row;">
                            <div className="z-10 flex flex-row h-full  text-xs font-semibold text-blue-60">
                                <div className="mt-1 mb-2">4.<a href={`/preliminaryopinion/${titleOpinionId}/plat`}>Recorded Plat</a>  </div>
                            </div>

                        </div>
                    </div>
                    <div className="col-span-8 grid grid-cols-8 grid-flow-row gap-x-8 gap-y-1  text-xs mb-4">
                        <div className="col-span-8 text-red-600">
                            {errorSection ?
                                <p className="flex flex-row gap-x-2 mb-2"><BsFillExclamationCircleFill />
                                    You have not provided all the required information in this section.</p>
                                : ''
                            }
                        </div>
                        <div className="col-span-3 font-semibold"> Is there a recorded plat ? </div>
                        <div className="col-span-1 flex-end">{state.values.recordedPlat}</div>
                        <div className="col-span-2 font-semibold"> Feet From Front : </div>
                        <div className="col-span-2 flex-end">{state.values.feetFromFront}</div>
                        <div className={`col-span-3 font-semibold ${invalidFields.sectionDetails && 'text-red-600'}`}>Book &amp; Page</div>
                        <div className="col-span-1 flex-end">{state.values.sectionDetails ? bookDetails.join(', ') : ''}</div>
                        <div className="col-span-2 font-semibold"> Feet From Side : </div>
                        <div className="col-span-2 flex-end">{state.values.feetFromSide}</div>
                        <div className="col-span-3 font-semibold"> Does survey and / or public record indicate a violation ? </div>
                        <div className="col-span-1 flex-end">{state.values.violation}</div>
                        <div className="col-span-2 font-semibold"> Feet From Side Street : </div>
                        <div className="col-span-2 flex-end">{state.values.feetFromSideStreet}</div>
                        <div className={`col-span-3 font-semibold ${invalidFields.accessToPublicRightOfWay && 'text-red-600'}`}> Access to Public Right of Way? </div>
                        <div className="col-span-1 flex-end">{state.values.accessToPublicRightOfWay}</div>
                        {state.values.accessToPublicRightOfWay == "Yes" &&
                            <>
                                <div className={`col-span-3 font-semibold ${invalidFields.accessType && 'text-red-600'}`}> Access Type(Direct or Over a Private Easement)? </div>
                                <div className="col-span-1 flex-end">{state.values.accessType}</div>
                            </>
                        }
                        <div className="col-span-2 font-semibold"> Feet From Rear : </div>
                        <div className="col-span-2 flex-end">{state.values.feetFromRear}</div>
                        <div className="col-span-6 font-semibold"> Easements/Other Matters on this Covenant </div>
                        <div className="col-span-6 flex-end">{state.values.easementsOthers}</div>


                    </div>
                </>
            }
        </div>
    );
};

export default RecordedPlatReview;